// The admin routes that work in the phone app's offline mode (BL-2342, ADR 066
// — Offline Mode). One list for every consumer: the sidebar and page greying
// (BL-2422) and the service worker's offline precache (BL-2341).

/**
 * The Storage & Sync page (the Offline page until BL-2566): the mode control and the change queue,
 * with the Storage sheet (the auctions saved on the device) opened over it by `?view=storage`.
 */
export const OFFLINE_PAGE_PATH = '/offline';

/**
 * Sidebar destinations that stay usable while offline. Everything else is
 * disabled in the sidebar and greyed when open (BL-2422).
 */
export const OFFLINE_CAPABLE_NAV_HREFS = ['/auctions', '/inventory', OFFLINE_PAGE_PATH] as const;

/**
 * The query parameter that names a sheet on the Storage & Sync page (BL-2566). Written in place
 * (BUG-2078), and carried onto the offline home document with the rest of the page's query (BUG-2079).
 */
export const OFFLINE_PAGE_VIEW_PARAM = 'view';

/** The sheets the Storage & Sync page opens by `?view=`. The page itself, the change queue, has none. */
export type OfflinePageView = 'storage';

/**
 * Whether a pathname belongs to an offline-capable area: one of
 * {@link OFFLINE_CAPABLE_NAV_HREFS} or a path below it.
 *
 * @param pathname - a path, optionally with a query string.
 * @returns true for `/auctions`, `/auctions/a1/lots`, `/inventory?x=1`; false
 *   for `/customers` and look-alikes such as `/auctionsX`.
 */
export function isOfflineCapablePath(pathname: string): boolean {
    const path = pathname.split(/[?#]/)[0];
    return OFFLINE_CAPABLE_NAV_HREFS.some((href) => path === href || path.startsWith(`${href}/`));
}

/**
 * Whether a path needs a connection the app doesn't have right now — the one
 * rule the sidebar's disabled items and the page gate share (BL-2422).
 *
 * @param pathname - a path, optionally with a query string.
 * @param isOffline - the app's offline mode (always false outside the app).
 * @returns true only while offline, for a path outside the offline-capable areas.
 */
export function isOfflineBlocked(pathname: string, isOffline: boolean): boolean {
    return isOffline && !isOfflineCapablePath(pathname);
}

/**
 * The Storage & Sync page's URL, optionally with a sheet open.
 *
 * @param view - the sheet to open; omitted opens the page (the change queue).
 * @returns e.g. `/offline?view=storage`.
 */
export function offlinePageHref(view?: OfflinePageView): string {
    return view ? `${OFFLINE_PAGE_PATH}?${OFFLINE_PAGE_VIEW_PARAM}=${view}` : OFFLINE_PAGE_PATH;
}

/**
 * The precached offline documents live under this prefix (BL-2341): `/offline/home`,
 * `/offline/auctions`, `/offline/lots?auction=…`, `/offline/inventory`. The trailing slash is
 * load-bearing, so the session-backed Offline page at `/offline` is not one of them.
 */
export const OFFLINE_DOCUMENT_PREFIX = '/offline/';

/**
 * Whether a pathname is one of the precached offline documents, which exist only for a
 * connection that failed and so always read from the device store (BL-2343).
 *
 * @param pathname - a path, optionally with a query string.
 * @returns true for `/offline/lots?auction=a1`; false for `/offline` and `/offlinex/a`.
 */
export function isOfflineDocumentPath(pathname: string): boolean {
    return pathname.split(/[?#]/)[0].startsWith(OFFLINE_DOCUMENT_PREFIX);
}

/**
 * Whether a pathname is one of the sign-in family's pages (`/auth/sign-in`, `/auth/accept-invite`,
 * …). They exist to establish a session with the server, so they are online screens whatever
 * mode the app was left in (BUG-2197): the device's stored session, which survives sign-out and
 * an expired session (ADR 066 — Offline Mode § 6.1 limit 3), must never answer their checks.
 *
 * @param pathname - a path, optionally with a query string.
 * @returns true for `/auth/sign-in?returnUrl=%2F`; false for `/auth` and `/authors`.
 */
export function isAuthPagePath(pathname: string): boolean {
    return pathname.split(/[?#]/)[0].startsWith('/auth/');
}

/**
 * The online screen an offline document stands in for, the reverse of the service worker's
 * fallback map (BL-2341), so an offline document can hand back to the real screen once the
 * server answers again.
 *
 * @param location - the offline document's pathname and query string.
 * @returns e.g. `/auctions/a1/items?lotPanel=l1` for `/offline/lots?auction=a1&lotPanel=l1`.
 */
export function onlinePathForOfflineDocument(location: { pathname: string; search: string }): string {
    const query = new URLSearchParams(location.search);
    const item = query.get('item');
    // BL-2430: the lots document mounts the Lots tab's own screen, so it carries its `lotPanel`.
    const lotPanel = query.get('lotPanel');
    switch (location.pathname) {
        case '/offline/auctions':
            return '/auctions';
        case '/offline/lots': {
            const auction = query.get('auction');
            if (!auction) return '/auctions';
            const base = `/auctions/${encodeURIComponent(auction)}/items`;
            return lotPanel ? `${base}?lotPanel=${encodeURIComponent(lotPanel)}` : base;
        }
        case '/offline/inventory':
            return item ? `/inventory?item=${encodeURIComponent(item)}` : '/inventory';
        default:
            return OFFLINE_PAGE_PATH;
    }
}

/** The precached offline documents, one per screen (BL-2341). The worker keeps its own copy. */
const OFFLINE_DOCUMENT_PATHS = ['/offline/home', '/offline/auctions', '/offline/lots', '/offline/inventory'] as const;

/** A path segment, decoded; a malformed escape stays raw. */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function withQuery(path: string, pairs: Array<[string, string | null]>): string {
  const params = new URLSearchParams();
  for (const [key, value] of pairs) if (value != null && value !== '') params.set(key, value);
  const query = params.toString();
  return query === '' ? path : `${path}?${query}`;
}

/**
 * The offline document that stands in for a location: the forward twin of
 * {@link onlinePathForOfflineDocument}, and the same map the service worker's fallback uses
 * (`resolveOfflineDocument`, apps/admin-web/service-worker, which keeps its own copy because the
 * worker is import-free; a test pins the two together). An auction's pages open its lots, another
 * offline-capable area opens its own document, an offline document is itself, the Storage & Sync
 * page opens the offline home with its own query, and anything else opens the offline home.
 *
 * BUG-2159: WebKit reports a fetch the worker answered with a redirect as not redirected, so Next
 * keeps the tapped address while it renders an offline document. The document uses this to move
 * to its own address.
 *
 * @param location - a pathname and query string (`search` with or without its `?`).
 * @returns e.g. `/offline/lots?auction=a1&lotPanel=l1` for `/auctions/a1/items?lotPanel=l1`.
 */
export function offlineDocumentFor(location: { pathname: string; search: string }): string {
  const path = location.pathname.length > 1 ? location.pathname.replace(/\/+$/, '') : location.pathname;
  const query = new URLSearchParams(location.search);
  const search = query.toString() === '' ? '' : `?${query.toString()}`;
  if ((OFFLINE_DOCUMENT_PATHS as readonly string[]).includes(path)) return path + search;
  const auction = /^\/auctions\/([^/]+)(?:\/|$)/.exec(path);
  if (auction) {
    return withQuery('/offline/lots', [
      ['auction', decodeSegment(auction[1])],
      ['lotPanel', query.get('lotPanel')],
    ]);
  }
  const area = OFFLINE_CAPABLE_NAV_HREFS.find((href) => path === href || path.startsWith(`${href}/`));
  if (area === undefined) return '/offline/home';
  if (path === OFFLINE_PAGE_PATH) return `/offline/home${search}`;
  if (area === OFFLINE_PAGE_PATH) return '/offline/home';
  return withQuery(`/offline${area}`, [['item', query.get('item')]]);
}

/**
 * The builds offline mode runs in: every labelled environment, now that every offline write is
 * queued (BL-2448 opened staging and `prod`, the production label, after BL-2431, BL-2447 and
 * BL-2448's writes). The same gate as the service worker's registration (BL-2341). An unlabelled
 * build, or any other label, stays off. The kill switch is removing an environment here and
 * deploying: the registrar then unregisters the worker and deletes its caches.
 */
export const OFFLINE_MODE_BUILD_ENVS: readonly string[] = ['local', 'dev', 'staging', 'prod'];

/**
 * @param auctogonEnv - `NEXT_PUBLIC_AUCTOGON_ENV`, baked per environment at build time.
 * @returns whether this build answers reads from the device store while offline.
 */
export function isOfflineModeBuild(auctogonEnv: string | undefined): boolean {
    return auctogonEnv != null && OFFLINE_MODE_BUILD_ENVS.includes(auctogonEnv);
}
